import type { AttachmentSource } from "utils/attachments/attachmentItems";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@outpave/ds";

interface useDisplayAttachmentsOptions {
  // Structural rather than a named app type: this reads assetUrl / extension /
  // name and nothing else, while admin's ImageAsset and employee's ReceiptAsset
  // disagree on whether assetUrl can be null.
  allAttachments: AttachmentSource[];
}

export function useDisplayAttachments({
  allAttachments = [],
}: useDisplayAttachmentsOptions) {
  const { t } = useTranslation();
  const [currentAsset, setCurrentAsset] = useState<{
    asset: AttachmentSource | null;
    index: number | null;
  }>({
    asset: null,
    index: null,
  });

  const changeCurrentAsset = (index: number | null) => {
    if (index === null) {
      setCurrentAsset({ asset: null, index: null });
    } else if (index < 0 || index >= allAttachments?.length) {
      console.error(`Invalid attachment index: ${index}`);
      return;
    } else if (allAttachments && allAttachments[index]) {
      setCurrentAsset({ asset: allAttachments[index], index });
    }
  };

  const printAsset = (imageUrl: string) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(
      `<html><head><title>${t("Asset")}</title></head><body><img src="${imageUrl}" alt=${t("Asset")}></body></html>`,
    );
    printWindow.document.close(); // Necessary for some browsers
    printWindow.focus(); // Necessary for some browsers

    // Wait for the image to load before printing
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const downloadAsset = async (imageUrl: string) => {
    // Try fetch/blob first (best UX if CORS is allowed)
    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Network response was not ok");
      const blob = await response.blob();
      const ext = blob.type.split("/").pop() || "bin";
      const filename = `receipt.${ext}`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      // Don't set target, let browser download
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      // Fallback: direct anchor download (no CORS required)
      try {
        // Extract extension from URL path (before query params)
        const urlPath = imageUrl.split("?")[0];
        const ext = urlPath.split(".").pop() || "bin";
        const filename = `receipt.${ext}`;
        const anchor = document.createElement("a");
        anchor.href = imageUrl;
        anchor.download = filename;
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (fallbackErr) {
        console.error("Download failed (fallback):", fallbackErr);
      }
    }
  };

  const downloadAllAssetsAsZip = async () => {
    if (!allAttachments?.length) return;

    try {
      const JSZipModule = await import("jszip");
      const JSZip = JSZipModule.default;
      const zip = new JSZip();

      const sanitizeFilename = (name: string) =>
        name
          .trim()
          .replace(/[\\/:*?"<>|]+/g, "-")
          .replace(/\s+/g, " ")
          .slice(0, 180);

      const usedNames = new Map<string, number>();
      const splitFilename = (filename: string) => {
        const trimmed = filename.trim();
        const lastDot = trimmed.lastIndexOf(".");
        const hasExtension =
          lastDot > 0 && lastDot < trimmed.length - 1 && trimmed !== "..";

        // Treat ".env" (or similar) as having no extension.
        if (!hasExtension || trimmed.startsWith(".")) {
          return { stem: trimmed, extWithDot: "" };
        }

        return {
          stem: trimmed.slice(0, lastDot),
          extWithDot: trimmed.slice(lastDot),
        };
      };

      const uniquify = (filenameWithExt: string) => {
        const { stem, extWithDot } = splitFilename(filenameWithExt);
        const key = `${stem}${extWithDot}`.toLowerCase();
        const count = usedNames.get(key) ?? 0;
        usedNames.set(key, count + 1);
        return count === 0
          ? `${stem}${extWithDot}`
          : `${stem} (${count + 1})${extWithDot}`;
      };

      const failures: { index: number; reason: string }[] = [];
      let addedFileCount = 0;

      await Promise.all(
        allAttachments.map(async (asset, index) => {
          if (!asset.assetUrl) return;

          try {
            const response = await fetch(asset.assetUrl, { mode: "cors" });
            if (!response.ok) {
              failures.push({
                index,
                reason:
                  `HTTP ${response.status} ${response.statusText || ""}`.trim(),
              });
              return;
            }

            const blob = await response.blob();
            const ext = blob.type.split("/").pop() || asset.extension || "bin";
            const preferredBase = asset.name
              ? sanitizeFilename(asset.name)
              : `receipt-${index + 1}`;
            const base = preferredBase || `receipt-${index + 1}`;
            const withExt = base.toLowerCase().endsWith(`.${ext.toLowerCase()}`)
              ? base
              : `${base}.${ext}`;
            const filename = uniquify(withExt);

            zip.file(filename, blob);
            addedFileCount += 1;
          } catch (error) {
            // Most common: CORS blocked or expired/invalid presigned URL.
            failures.push({
              index,
              reason: error instanceof Error ? error.message : "Fetch failed",
            });
          }
        }),
      );

      if (failures.length) {
        console.warn("Some assets failed to fetch for zip download:", failures);
        toast.error(
          t("Some receipts could not be downloaded Please try again"),
        );
      }

      // If we couldn't fetch anything (e.g., CORS blocked for all assets), fallback to direct downloads.
      if (addedFileCount === 0) {
        toast.error(
          t(
            "Download as zip isn't available for these receipts Downloading files individually instead",
          ),
        );
        allAttachments.forEach((asset) => {
          if (!asset.assetUrl) return;
          const anchor = document.createElement("a");
          anchor.href = asset.assetUrl;
          anchor.target = "_blank";
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        });
        return;
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const objectUrl = URL.createObjectURL(zipBlob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "receipts.zip";
      anchor.target = "_blank";

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Download zip failed:", err);
      toast.error(t("Unable to download receipts Please try again"));
    }
  };

  return {
    currentAsset,
    changeCurrentAsset,
    printAsset,
    downloadAsset,
    downloadAllAssetsAsZip,
  };
}
